// Bloc « Références » de la fiche véhicule : ce que CE camion consomme.
//
// C'est la fiche papier que l'atelier garde dans un classeur — plaquettes,
// pneus, huile, filtres — mise là où le mécanicien la cherche.
//
// Quand le module Stock est souscrit, chaque ligne dit EN PLUS ce qu'il reste
// en magasin. C'est la question réelle : « on change les plaquettes, est-ce
// qu'il en reste un jeu ou faut-il commander ? » Sans le module, le bloc reste
// utile — il donne la référence à commander — simplement sans la colonne stock.
import { useCallback, useEffect, useState } from "react";

import api from "../api";
import { useModules } from "../modules";
import ModaleConfirmation from "./ModaleConfirmation";
import { UNITES, libelleUnite } from "../unites";

// Les libellés vivent ici, les valeurs côté serveur (schemas.PosteReference).
// Un poste ajouté au serveur sans l'être ici s'affiche par sa valeur brute
// plutôt que de casser l'écran.
const POSTES = [
  { valeur: "plaquettes_avant", label: "Plaquettes avant" },
  { valeur: "plaquettes_arriere", label: "Plaquettes arrière" },
  { valeur: "disques_avant", label: "Disques avant" },
  { valeur: "disques_arriere", label: "Disques arrière" },
  { valeur: "pneus_avant", label: "Pneus avant" },
  { valeur: "pneus_arriere", label: "Pneus arrière" },
  { valeur: "huile_moteur", label: "Huile moteur" },
  { valeur: "filtre_huile", label: "Filtre à huile" },
  { valeur: "filtre_air", label: "Filtre à air" },
  { valeur: "filtre_gasoil", label: "Filtre à gasoil" },
  { valeur: "essuie_glaces", label: "Essuie-glaces" },
  { valeur: "batterie", label: "Batterie" },
  { valeur: "autre", label: "Autre" },
];


const libellePoste = (valeur) =>
  POSTES.find((p) => p.valeur === valeur)?.label || valeur;

const NOUVELLE_VIDE = {
  poste: "plaquettes_avant",
  libelle_libre: "",
  reference: "",
  marque: "",
  quantite_habituelle: "",
  unite: "piece",
  notes: "",
};

function messageErreur(err, defaut) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return defaut;
}

function nettoyer(brut) {
  const corps = {};
  Object.entries(brut).forEach(([cle, valeur]) => {
    if (valeur !== "" && valeur !== null && valeur !== undefined) corps[cle] = valeur;
  });
  return corps;
}

export default function ReferencesVehicule({ vehiculeId, peutModifier }) {
  const modules = useModules();

  const [references, setReferences] = useState(null); // null = pas encore chargé
  const [dispo, setDispo] = useState([]); // rapprochement avec le stock
  const [erreur, setErreur] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [nouvelle, setNouvelle] = useState(NOUVELLE_VIDE);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(() => {
    api
      .get(`/vehicules/${vehiculeId}/references`)
      .then(({ data }) => setReferences(data))
      .catch(() => setErreur("Impossible de charger les références."));
  }, [vehiculeId]);

  useEffect(charger, [charger]);

  useEffect(() => {
    if (!modules.module_stock) return;
    // Un échec ici ne doit pas vider le bloc : les références restent
    // affichées, simplement sans la colonne stock.
    api
      .get(`/vehicules/${vehiculeId}/stock-disponible`)
      .then(({ data }) => setDispo(data))
      .catch(() => setDispo([]));
  }, [vehiculeId, modules.module_stock, references]);

  const ajouter = (e) => {
    e.preventDefault();
    setEnCours(true);
    setErreur("");
    api
      .post(`/vehicules/${vehiculeId}/references`, nettoyer(nouvelle))
      .then(() => {
        setNouvelle(NOUVELLE_VIDE);
        setOuvert(false);
        charger();
      })
      // Un poste déjà renseigné répond 409 : deux « plaquettes avant »
      // différentes sur le même camion ne veulent rien dire.
      .catch((err) => setErreur(messageErreur(err, "Ajout impossible.")))
      .finally(() => setEnCours(false));
  };

  const retirer = (ref) => {
    setConfirmation({
      titre: "Retirer cette référence ?",
      sousTitre: libellePoste(ref.poste),
      message: "Elle ne sera plus proposée pour ce véhicule. Le stock n'est pas touché.",
      libelleConfirmer: "Retirer",
      danger: true,
      onConfirmer: async () => {
        await api.delete(`/references/${ref.id}`);
        charger();
      },
    });
  };

  const stockDe = (ref) => dispo.find((d) => d.poste === ref.poste);

  return (
    <section className="carte">
      {confirmation && (
        <ModaleConfirmation {...confirmation} onFermer={() => setConfirmation(null)} />
      )}
      <div className="entete-section">
        <h3>Références</h3>
        {peutModifier && (
          <button
            className="btn"
            onClick={() => {
              setOuvert((v) => !v);
              setErreur("");
            }}
          >
            {ouvert ? "Annuler" : "+ Ajouter"}
          </button>
        )}
      </div>

      {erreur && <p className="erreur">{erreur}</p>}

      {ouvert && (
        <form className="formulaire" onSubmit={ajouter}>
          <div className="grille-form">
            <label className="champ">
              <span className="champ-label">Poste *</span>
              <select
                value={nouvelle.poste}
                onChange={(e) => setNouvelle({ ...nouvelle, poste: e.target.value })}
              >
                {POSTES.map((p) => (
                  <option key={p.valeur} value={p.valeur}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            {/* Le serveur EXIGE un libellé quand le poste est « autre » : une
                ligne « autre — XY12 » oblige à ouvrir le catalogue du
                fabricant pour savoir de quelle pièce on parle. */}
            {nouvelle.poste === "autre" && (
              <label className="champ">
                <span className="champ-label">Intitulé *</span>
                <input
                  required
                  placeholder="Courroie accessoire"
                  value={nouvelle.libelle_libre}
                  onChange={(e) =>
                    setNouvelle({ ...nouvelle, libelle_libre: e.target.value })
                  }
                />
              </label>
            )}
            <label className="champ">
              <span className="champ-label">Référence *</span>
              <input
                required
                placeholder="BP-4412"
                value={nouvelle.reference}
                onChange={(e) => setNouvelle({ ...nouvelle, reference: e.target.value })}
              />
            </label>
            <label className="champ">
              <span className="champ-label">Marque</span>
              <input
                value={nouvelle.marque}
                onChange={(e) => setNouvelle({ ...nouvelle, marque: e.target.value })}
              />
            </label>
            <label className="champ">
              <span className="champ-label">Quantité habituelle</span>
              <input
                type="number"
                min="0"
                step="any"
                value={nouvelle.quantite_habituelle}
                onChange={(e) =>
                  setNouvelle({ ...nouvelle, quantite_habituelle: e.target.value })
                }
              />
            </label>
            <label className="champ">
              <span className="champ-label">Unité</span>
              <select
                value={nouvelle.unite}
                onChange={(e) => setNouvelle({ ...nouvelle, unite: e.target.value })}
              >
                {UNITES.map((u) => (
                  <option key={u.valeur} value={u.valeur}>
                    {u.singulier}
                  </option>
                ))}
              </select>
            </label>
            <label className="champ champ-large">
              <span className="champ-label">Remarque</span>
              <input
                value={nouvelle.notes}
                onChange={(e) => setNouvelle({ ...nouvelle, notes: e.target.value })}
              />
            </label>
          </div>
          {modules.module_stock && (
            <p className="sous-info">
              Si cette référence existe au stock, la fiche affichera ce qu’il
              reste en magasin — le rapprochement se fait sur la référence.
            </p>
          )}
          <div className="actions-form">
            <button className="btn-accent" type="submit" disabled={enCours}>
              {enCours ? "Ajout…" : "Ajouter"}
            </button>
          </div>
        </form>
      )}

      {references === null ? (
        <p className="note-discrete">Chargement…</p>
      ) : references.length === 0 ? (
        <p className="vide">
          Aucune référence renseignée. Elles disent quoi commander pour ce
          véhicule, sans avoir à rouvrir une ancienne facture.
        </p>
      ) : (
        <table className="tableau">
          <thead>
            <tr>
              <th>Poste</th>
              <th>Référence</th>
              <th>Quantité</th>
              {modules.module_stock && <th>En magasin</th>}
              {peutModifier && <th></th>}
            </tr>
          </thead>
          <tbody>
            {references.map((r) => {
              const s = stockDe(r);
              return (
                <tr key={r.id}>
                  <td>
                    {r.poste === "autre" && r.libelle_libre
                      ? r.libelle_libre
                      : libellePoste(r.poste)}
                    {r.notes && (
                      <span className="note-discrete"> · {r.notes}</span>
                    )}
                  </td>
                  <td>
                    {r.reference}
                    {r.marque && <span className="note-discrete"> · {r.marque}</span>}
                  </td>
                  <td>
                    {r.quantite_habituelle
                      ? `${r.quantite_habituelle} ${libelleUnite(r.unite, r.quantite_habituelle)}`
                      : "—"}
                  </td>
                  {modules.module_stock && (
                    <td>
                      {/* Trois états à distinguer, et le troisième compte :
                          « pas au catalogue » n'est pas « zéro en stock ». */}
                      {!s || s.article_id === null ? (
                        <span className="note-discrete">Pas au stock</span>
                      ) : (
                        <>
                          <strong>{s.en_stock}</strong> {libelleUnite(s.unite, s.en_stock)}{" "}
                          {s.suffisant === false && (
                            <span className="badge">à commander</span>
                          )}
                        </>
                      )}
                    </td>
                  )}
                  {peutModifier && (
                    <td className="actions-cellule">
                      <button
                        className="lien-action lien-danger"
                        onClick={() => retirer(r)}
                      >
                        Retirer
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
